import { createContext } from "react"
import type { ServerboundMessage } from "./protocol.ts"

/**
 * The lifecycle of our WebSocket connection, surfaced to UI.
 *
 * - `idle` — we have no live session; the user is on the home screen.
 * - `connecting` — user-initiated connect in progress (initial join, manual
 *   rejoin); we haven't yet been authenticated.
 * - `connected` — socket is open and the server has confirmed our join (we
 *   received a clientbound `join_room`).
 * - `reconnecting` — we lost a previously-connected socket and the auto-
 *   reconnect loop is trying to get it back. `attempt` is 1-indexed.
 * - `failed` — the reconnect loop gave up, or an initial connect attempt
 *   bounced. The UI surfaces a manual "Try again".
 * - `kicked` — the server explicitly removed us (host kick). Distinct from
 *   `failed` because retrying won't help; the player needs to rejoin from
 *   scratch.
 */
export type ConnectionStatus =
    | { kind: "idle" }
    | { kind: "connecting" }
    | { kind: "connected" }
    | { kind: "reconnecting"; attempt: number }
    | { kind: "failed"; reason: string }
    | { kind: "kicked"; reason: string }

export interface WebSocketContextType {
    sendMessage: (message: ServerboundMessage) => void
    connect: (roomId: string) => Promise<void>
    disconnect: () => void
    createRoom: (params?: { boxId: string, p?: string, c?: string, ownerToken?: string }) => Promise<{ roomCode: string, joinedExisting: boolean }>
    rejoin: () => Promise<boolean>
    /** Manual trigger for the "Try again" button shown when auto-reconnect
     *  exhausts its attempts. Resets the attempt counter and immediately
     *  tries one more rejoin. */
    retryReconnect: () => void
    hasRejoinState: () => boolean
}

export const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined)
